/**
 * Browser health sampler for long-lived sessions. Framework-independent: the caller supplies
 * counters and sinks (wired up in `src/plugins/browserDiagnostics.ts`).
 *
 * Signals, strongest first:
 * - Unclean exits. Every sampled tab holds a Web Lock and keeps its latest sample in
 *   localStorage; `pagehide` removes the record. A record whose lock is no longer held belongs
 *   to a tab that died without unloading (renderer crash / OOM, tab discard, mobile OS kill).
 *   That means "ended uncleanly", not proof of OOM.
 * - App counters (mounted components, requests, queries, sockets): exact, but only as good as
 *   the instrumentation that feeds them.
 * - DOM node count (all browsers) and long tasks (Chromium only).
 * - JS heap via `performance.memory`: Chromium only, nonstandard, quantized and refreshed
 *   infrequently unless precise memory info is enabled. Treat it as coarse.
 *
 * Growth is judged per route. Each metric keeps the lowest value seen on every route, and a
 * growth warning needs the last SUSTAIN_SAMPLES samples ALL above their own route's floor by
 * the threshold. A heavy page does not trip it (its floor is heavy); memory or components
 * retained across navigations do. Thresholds are the caller's guesses, not browser limits.
 *
 * Not covered: OS RAM / GPU, heap snapshots, forced GC, retry queues. Samples taken offline
 * are kept in the bounded history but not sent. Hidden tabs are throttled or frozen, so
 * samples with long gaps are excluded from warnings.
 */

export type Counters = Record<string, number>;

export type DiagnosticSample = {
  session: string;
  timestamp: string;
  uptimeMinutes: number;
  sampleGapSeconds: number;
  /** Stable route name, never a URL or record ID. */
  routeName: string;
  hidden: boolean;
  online: boolean;
  visibilityChanges: number;
  domNodes: number;
  /** Since the previous sample; absent where the Long Tasks API is unsupported. */
  longTasks?: number;
  longTaskMs?: number;
  heapUsedMiB?: number;
  counters: Counters;
  counterReadFailed: boolean;
};

export type GrowthFinding = { metric: string; excess: number; threshold: number };

export type WarningReport = {
  reasons: string[];
  growth: GrowthFinding[];
  current: DiagnosticSample;
  history: DiagnosticSample[];
};

export type UncleanExitReport = {
  previous: DiagnosticSample;
  startedAt: string;
  lastSeenAt: string;
  /** This page load replaces a tab the browser discarded (Chromium `document.wasDiscarded`). */
  wasDiscarded: boolean;
};

export type DiagnosticsOptions = {
  getRouteName: () => string;
  /** Return a fresh numeric snapshot; never stores, responses or DOM nodes. */
  getCounters: () => Counters;
  onHeartbeat: (sample: DiagnosticSample) => void;
  onWarning: (report: WarningReport) => void;
  /** Sustained ceilings. Keys: counter names, `heapUsedMiB`, `domNodes`. */
  limits?: Record<string, number>;
  /** Sustained growth above the per-route floor. Same keys as `limits`. */
  growth?: Record<string, number>;
};

const MINUTE = 60_000;
const SAMPLE_MS = MINUTE;
const HEARTBEAT_MS = 5 * MINUTE;
const WARMUP_MINUTES = 5;
const MAX_GAP_SECONDS = 120;
const HISTORY_LIMIT = 30;
const SUSTAIN_SAMPLES = 5;
const WARNING_COOLDOWN_MS = 15 * MINUTE;
const MAX_WARNINGS = 4;
const MAX_ROUTES = 200;
const MAX_UNCLEAN_REPORTS = 3;
const STORAGE_PREFIX = "orbis.browserDiagnostics.v1.";
const LOCK_PREFIX = "orbis-browser-diagnostics-";

type StoredSession = { startedAt: string; lastSeenAt: string; sample: DiagnosticSample };

let activeStop: (() => void) | undefined;

function getStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function metricsOf(sample: DiagnosticSample): Record<string, number> {
  const metrics: Record<string, number> = { ...sample.counters, domNodes: sample.domNodes };
  if (sample.heapUsedMiB !== undefined) metrics.heapUsedMiB = sample.heapUsedMiB;
  return metrics;
}

function readHeapMiB(): number | undefined {
  try {
    const bytes = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory
      ?.usedJSHeapSize;
    if (typeof bytes === "number" && Number.isFinite(bytes) && bytes > 0) {
      return Math.round(bytes / 1_048_576);
    }
  } catch {
    /* Optional API. */
  }
  return undefined;
}

/**
 * Reports tabs from earlier page loads that never reached `pagehide`, then deletes their
 * records. Run on every page load where diagnostics are enabled, even if this load is not
 * sampled, so leftovers are not stranded. Needs the Web Locks API; otherwise a no-op.
 */
export async function reportUncleanExits(
  onUncleanExit: (report: UncleanExitReport) => void
): Promise<void> {
  try {
    if (globalThis.window === undefined || !navigator.locks?.query) return;
    const storage = getStorage();
    if (!storage) return;

    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    if (keys.length === 0) return;

    const { held = [] } = await navigator.locks.query();
    const heldNames = new Set(held.map((lock) => lock.name));
    const wasDiscarded = (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
    let reported = 0;

    for (const key of keys) {
      // A live tab (possibly frozen in the background) still holds its lock.
      if (heldNames.has(LOCK_PREFIX + key.slice(STORAGE_PREFIX.length))) continue;
      const raw = storage.getItem(key);
      storage.removeItem(key);
      if (!raw || reported >= MAX_UNCLEAN_REPORTS) continue;
      try {
        const stored = JSON.parse(raw) as StoredSession;
        if (!stored?.sample) continue;
        reported++;
        onUncleanExit({
          previous: stored.sample,
          startedAt: stored.startedAt,
          lastSeenAt: stored.lastSeenAt,
          wasDiscarded,
        });
      } catch {
        /* Corrupt record: already removed. */
      }
    }
  } catch {
    /* Diagnostics must not interrupt application work. */
  }
}

/** Starts sampling once per page load. Later calls get a no-op stop; only the first owns it. */
export function startBrowserDiagnostics(options: DiagnosticsOptions): () => void {
  if (globalThis.window === undefined || activeStop) return () => {};

  const started = Date.now();
  const startedAt = new Date(started).toISOString();
  const session =
    globalThis.crypto?.randomUUID?.() ??
    // Fallback correlation id only; nothing security-relevant depends on it.
    // eslint-disable-next-line sonarjs/pseudo-random
    `${started.toString(36)}-${Math.random().toString(36).slice(2)}`;
  const storageKey = STORAGE_PREFIX + session;
  const storage = getStorage();

  const history: DiagnosticSample[] = [];
  const floors = new Map<string, Map<string, number>>();
  let previous = started;
  let lastHeartbeat = -Infinity;
  let lastWarning = -Infinity;
  let warnings = 0;
  let visibilityChanges = 0;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lockHeld = false;
  let releaseLock: (() => void) | undefined;

  let longTasks = 0;
  let longTaskMs = 0;
  let longTaskObserver: PerformanceObserver | undefined;
  try {
    if (PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
      longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          longTasks++;
          longTaskMs += entry.duration;
        }
      });
      longTaskObserver.observe({ type: "longtask" });
    }
  } catch {
    longTaskObserver = undefined;
  }

  const persist = (sample: DiagnosticSample | undefined) => {
    // Only write once the lock is held, so another tab never sees a record without its lock.
    if (!sample || !lockHeld || !storage) return;
    try {
      const stored: StoredSession = { startedAt, lastSeenAt: sample.timestamp, sample };
      storage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      /* Quota or privacy mode: unclean-exit detection is best effort. */
    }
  };
  const forget = () => {
    try {
      storage?.removeItem(storageKey);
    } catch {
      /* Best effort. */
    }
  };

  const onVisibility = () => {
    visibilityChanges++;
  };
  const onPageHide = () => forget();
  const onPageShow = (event: PageTransitionEvent) => {
    // Restored from the back/forward cache: the tab is alive again.
    if (event.persisted) persist(history.at(-1));
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer !== undefined) globalThis.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    longTaskObserver?.disconnect();
    forget();
    releaseLock?.();
    history.length = 0;
    floors.clear();
    if (activeStop === stop) activeStop = undefined;
  };
  activeStop = stop;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);

  try {
    navigator.locks
      ?.request(
        LOCK_PREFIX + session,
        () =>
          new Promise<void>((resolve) => {
            if (stopped) return resolve();
            lockHeld = true;
            releaseLock = resolve;
            persist(history.at(-1));
          })
      )
      .catch(() => {});
  } catch {
    /* No Web Locks: sampling still works, unclean-exit detection does not. */
  }

  const isEligible = (s: DiagnosticSample) =>
    s.uptimeMinutes >= WARMUP_MINUTES && s.sampleGapSeconds <= MAX_GAP_SECONDS;

  const updateFloors = (s: DiagnosticSample) => {
    let route = floors.get(s.routeName);
    if (!route) {
      if (floors.size >= MAX_ROUTES) return;
      route = new Map();
      floors.set(s.routeName, route);
    }
    for (const [metric, value] of Object.entries(metricsOf(s))) {
      const floor = route.get(metric);
      if (floor === undefined || value < floor) route.set(metric, value);
    }
  };

  const evaluate = (): { reasons: string[]; growth: GrowthFinding[] } => {
    const reasons: string[] = [];
    const growth: GrowthFinding[] = [];
    const recent = history.slice(-SUSTAIN_SAMPLES);
    if (recent.length < SUSTAIN_SAMPLES || !recent.every((s) => isEligible(s)))
      return { reasons, growth };

    for (const [metric, limit] of Object.entries(options.limits ?? {})) {
      if (recent.every((s) => (metricsOf(s)[metric] ?? -Infinity) > limit)) {
        reasons.push(`${metric}_above_limit`);
      }
    }

    for (const [metric, threshold] of Object.entries(options.growth ?? {})) {
      const excesses: number[] = [];
      for (const s of recent) {
        const value = metricsOf(s)[metric];
        const floor = floors.get(s.routeName)?.get(metric);
        if (value === undefined || floor === undefined) break;
        excesses.push(value - floor);
      }
      if (excesses.length < recent.length) continue;
      const excess = Math.min(...excesses);
      if (excess >= threshold) {
        reasons.push(`${metric}_growth`);
        growth.push({ metric, excess, threshold });
      }
    }
    return { reasons, growth };
  };

  function sample() {
    if (stopped) return;
    try {
      const now = Date.now();
      const counters: Counters = {};
      let counterReadFailed = false;
      try {
        for (const [key, value] of Object.entries(options.getCounters())) {
          if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
            counters[key] = value;
          }
        }
      } catch {
        counterReadFailed = true;
      }

      let routeName = "unknown";
      try {
        routeName = String(options.getRouteName()).slice(0, 100);
      } catch {
        /* Fallback. */
      }

      const heapUsedMiB = readHeapMiB();
      const current: DiagnosticSample = {
        session,
        timestamp: new Date(now).toISOString(),
        uptimeMinutes: Math.max(0, (now - started) / MINUTE),
        sampleGapSeconds: Math.max(0, (now - previous) / 1000),
        routeName,
        hidden: document.hidden,
        online: navigator.onLine,
        visibilityChanges,
        // A live HTMLCollection length: far cheaper than building a static querySelectorAll list.
        // eslint-disable-next-line unicorn/prefer-query-selector
        domNodes: document.getElementsByTagName("*").length,
        ...(longTaskObserver ? { longTasks, longTaskMs: Math.round(longTaskMs) } : {}),
        ...(heapUsedMiB === undefined ? {} : { heapUsedMiB }),
        counters,
        counterReadFailed,
      };
      previous = now;
      longTasks = 0;
      longTaskMs = 0;
      history.push(current);
      if (history.length > HISTORY_LIMIT) history.shift();
      if (isEligible(current)) updateFloors(current);
      persist(current);

      // navigator.onLine is a hint, not a guarantee that Sentry is reachable.
      if (!current.online) return;
      if (now - lastHeartbeat >= HEARTBEAT_MS) {
        lastHeartbeat = now;
        try {
          options.onHeartbeat(structuredClone(current));
        } catch {
          /* No retry queue. */
        }
      }

      if (warnings >= MAX_WARNINGS || now - lastWarning < WARNING_COOLDOWN_MS) return;
      const { reasons, growth } = evaluate();
      if (reasons.length === 0) return;
      lastWarning = now;
      warnings++;
      try {
        options.onWarning({
          reasons,
          growth,
          current: structuredClone(current),
          history: structuredClone(history),
        });
      } catch {
        /* Diagnostics must not interrupt application work. */
      }
    } finally {
      if (!stopped) timer = globalThis.setTimeout(sample, SAMPLE_MS);
    }
  }

  sample();
  return stop;
}
